import forge from 'apis/forge';
import {
	Frame,
	StyleVisualEdit,
	VisualEdit,
	VisualEditFrameEntry,
} from 'dragonmouse-shared/interfaces';
import { createHash } from 'dragonmouse-shared/util';
import eventManager from 'events/EventManager';
import store from 'redux/store';
import { v4 as uuid } from 'uuid';

// Returns the per-site VisualEditFrameEntry for a frame, or undefined.
// Each site (frame version) gets its own entry; this lookup is the gate
// that all visual-edit reads/writes share.
export function findVisualEditEntry(
	frame: Frame,
	siteId: string
): VisualEditFrameEntry | undefined {
	return frame.entries?.find(
		(e): e is VisualEditFrameEntry =>
			e.kind === 'visualEdit' && 'siteId' in e && e.siteId === siteId
	);
}

// Visual-edit operations split by destination:
//   - saveEditToFrame / removeEditFromFrame: write to the frame's data only.
//     Use when the iframe is already in the desired state (e.g. a devigner
//     commit — the user just typed in the iframe and we're recording it).
//   - pushEditToIframe: tell the iframe to apply an edit now. Use when the
//     edit originated outside the iframe (e.g. a panel control). The caller
//     must ALSO call saveEditToFrame to persist — these functions are atomic
//     and the caller composes them explicitly.
//
// Frame is always looked up from the live Redux store rather than a closure,
// so concurrent writes (devigner commit + panel edit) don't race.

// Persists a visual edit to the frame's per-site VisualEditFrameEntry.
// Does NOT touch the iframe.
//
// Returns a Promise that resolves when the persistence has reached the server.
export async function saveEditToFrame(
	frameId: string,
	edit: VisualEdit,
	findExisting: (e: VisualEdit) => boolean,
	siteId: string
): Promise<void> {
	const frame = store.getState().db.frames.find((f) => f.id === frameId);
	if (!frame) return;

	const visualEditEntry = findVisualEditEntry(frame, siteId);

	if (visualEditEntry) {
		const existingEditIndex = visualEditEntry.visualEdits.findIndex(findExisting);

		const updatedVisualEdits = [...visualEditEntry.visualEdits];
		if (existingEditIndex >= 0) {
			updatedVisualEdits[existingEditIndex] = edit;
		} else {
			updatedVisualEdits.push(edit);
		}

		const updatedEntry: VisualEditFrameEntry = {
			...visualEditEntry,
			visualEdits: updatedVisualEdits,
			hash: createHash(updatedVisualEdits) || undefined,
		};

		await forge.updateFrameEntry({ frameId: frame.id, entry: updatedEntry });
	} else {
		const newEntry: VisualEditFrameEntry = {
			id: uuid(),
			kind: 'visualEdit',
			createdAt: new Date(),
			deletedAt: null,
			siteId: siteId,
			visualEdits: [edit],
			hash: createHash([edit]) || undefined,
		};

		// Only flip visualEditActive if this edit is for the current site
		const writes: Promise<unknown>[] = [
			forge.addFrameEntry({ frameId: frame.id, entry: newEntry }),
		];
		if (siteId === frame.siteId) {
			writes.push(
				forge.updateFrame({
					update: {
						id: frame.id,
						metadata: { ...frame.metadata, visualEditActive: true },
					},
				})
			);
		}
		await Promise.all(writes);
	}
}

// Tells the iframe to apply a visual edit now. Does NOT persist —
// caller must also call saveEditToFrame. Routed via EventManager so
// the panel doesn't need direct access to the iframe ref.
export function pushEditToIframe(frameId: string, edit: VisualEdit): void {
	eventManager.emit('apply-visual-edit-to-iframe', { frameId, edit });
}

// Set one or more CSS properties on an element via the visual-edit system.
// Reads the live store, merges with the element's existing StyleVisualEdit
// (so unrelated properties aren't clobbered), persists to the frame, and
// pushes to the iframe.
//
// One canonical implementation shared by all panel-side style controls (color,
// typography, padding, etc.) — keeps the merge-and-push logic consistent and
// avoids drift between controls. Accepts a Record so callers like padding
// (which sets four sides at once in single mode) batch into one round-trip
// instead of firing multiple concurrent writes.
export async function applyStyleProperties(
	frameId: string,
	siteId: string,
	elementId: string,
	styles: Record<string, string>
): Promise<void> {
	const frame = store.getState().db.frames.find((f) => f.id === frameId);
	const entry = frame ? findVisualEditEntry(frame, siteId) : undefined;
	const existing = entry?.visualEdits.find(
		(e): e is StyleVisualEdit => e.kind === 'style' && e.elementId === elementId
	);
	const styleEdit: StyleVisualEdit = {
		kind: 'style',
		elementId,
		styles: { ...(existing?.styles ?? {}), ...styles },
	};
	await saveEditToFrame(
		frameId,
		styleEdit,
		(e) => e.kind === 'style' && e.elementId === elementId,
		siteId
	);
	pushEditToIframe(frameId, styleEdit);
}

// Removes a visual edit from the frame's per-site VisualEditFrameEntry.
// Deletes the entry entirely if it would become empty (and clears
// visualEditActive in that case). Does NOT touch the iframe.
export function removeEditFromFrame(
	frameId: string,
	findExisting: (e: VisualEdit) => boolean,
	siteId: string
): void {
	const frame = store.getState().db.frames.find((f) => f.id === frameId);
	if (!frame) return;

	const visualEditEntry = findVisualEditEntry(frame, siteId);
	if (!visualEditEntry) return;

	const existingEditIndex = visualEditEntry.visualEdits.findIndex(findExisting);
	if (existingEditIndex < 0) return;

	const updatedVisualEdits = [...visualEditEntry.visualEdits];
	updatedVisualEdits.splice(existingEditIndex, 1);

	if (updatedVisualEdits.length === 0) {
		forge.deleteFrameEntry({ frameId: frame.id, entryId: visualEditEntry.id });

		if (frame.metadata.visualEditActive) {
			forge.updateFrame({
				update: {
					id: frame.id,
					metadata: { ...frame.metadata, visualEditActive: false },
				},
			});
		}
	} else {
		const updatedEntry: VisualEditFrameEntry = {
			...visualEditEntry,
			visualEdits: updatedVisualEdits,
			hash: createHash(updatedVisualEdits) || undefined,
		};
		forge.updateFrameEntry({ frameId: frame.id, entry: updatedEntry });
	}
}
